import React, { useEffect, useState } from "react"
import { Tooltip } from "antd"
import { WebsocketProvider } from "y-websocket"

interface PresenceUser {
  clientId: number
  name: string
  color: string
  isYou: boolean
}

interface OnlinePresenceProps {
  provider: WebsocketProvider | null
  currentUserId?: string
}

const OnlinePresence: React.FC<OnlinePresenceProps> = ({
  provider,
  currentUserId,
}) => {
  const [users, setUsers] = useState<PresenceUser[]>([])

  useEffect(() => {
    if (!provider) return

    const updateUsers = () => {
      const states = provider.awareness.getStates()
      const present: PresenceUser[] = []

      states.forEach((state, clientId) => {
        if (state.user && state.user.name) {
          present.push({
            clientId,
            name: state.user.name,
            color: state.user.color || "#6b7280",
            isYou: state.user.name === currentUserId,
          })
        }
      })

      // Deduplicate by name (multiple tabs = multiple connections for same user)
      const seen = new Set<string>()
      const deduplicated = present.filter((user) => {
        if (seen.has(user.name)) return false
        seen.add(user.name)
        return true
      })

      deduplicated.sort((a, b) => (a.isYou === b.isYou ? 0 : a.isYou ? -1 : 1))
      setUsers(deduplicated)
    }

    updateUsers()
    provider.awareness.on("change", updateUsers)

    return () => {
      provider.awareness.off("change", updateUsers)
    }
  }, [provider, currentUserId])

  if (users.length === 0) return null

  return (
    <div className="flex items-center gap-1">
      <div className="flex -space-x-1.5">
        {users.slice(0, 5).map((user) => (
          <Tooltip key={user.clientId} title={user.isYou ? `${user.name} (you)` : user.name}>
            <div
              className="w-6 h-6 rounded-full border-2 border-white flex items-center justify-center text-[10px] font-semibold text-white shadow-sm"
              style={{ backgroundColor: user.color }}
            >
              {user.name.charAt(0).toUpperCase()}
            </div>
          </Tooltip>
        ))}
        {users.length > 5 && (
          <Tooltip title={users.slice(5).map((u) => u.name).join(", ")}>
            <div className="w-6 h-6 rounded-full border-2 border-white flex items-center justify-center text-[10px] font-semibold text-gray-600 bg-gray-200 shadow-sm">
              +{users.length - 5}
            </div>
          </Tooltip>
        )}
      </div>
      <span className="text-xs text-gray-500 ml-1">
        {users.length} online
      </span>
    </div>
  )
}

export default OnlinePresence
